import React from 'react';
import {
  DatePicker,
  MuiPickersUtilsProvider,
  KeyboardDateTimePicker,
} from '@material-ui/pickers';
import { Button, Grid, TextField, Typography, Input } from '@material-ui/core';
import ptBR from 'dayjs/locale/pt-br';
import DayjsUtils from '@date-io/dayjs';
import { useFormik, getIn } from 'formik';
import dayjs from 'dayjs';
import * as yup from 'yup';
import ActionsButtonsBaseModal from 'components/atoms/ActionsButtonsBaseModal';
import ButtonWithLoading from 'components/atoms/ButtonWithLoading';
import { postStartDailyBulletin } from 'services/daily-bulletin';
import { IDailyBulletinModalFinishProps } from './types';
import useStyles from './style';

const DEFAULT_HELPER_TEXT = 'Obrigatório';
const DEFAULT_OK_TEXT = 'Obrigatório';
const DEFAULT_ERROR_TEXT = 'Preenchimento inválido.';

const FORM_ID = 'DAILY_BULLETIN_FORM_FINISH';

const MAX_DATE = dayjs().toISOString();

const DailyBulletinModalStart = ({
  children,
  open,
  onClose,
  selectedRow,
  reloadData,
}: IDailyBulletinModalFinishProps) => {
  const classes = useStyles();
  const [loadingSaveForm, setLoadingSaveForm] = React.useState<boolean>(false);

  const validationSchema = yup.object({
    activityPlanId: yup.number().nullable(),
    activityRealId: yup.number().nullable(),
    activityDiverseId: yup.number().nullable(),
    realStart: yup
      .mixed()
      .required(DEFAULT_OK_TEXT)
      .test('is-greater-than', DEFAULT_ERROR_TEXT, value => {
        const minDate = new Date();
        const test = dayjs(value);
        return (
          (dayjs(value).isValid() && test.isSame(minDate, 'minute')) ||
          test.isBefore(minDate, 'minute')
        );
      }),
    maintenanceType: yup.string().nullable(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      activityRealId: selectedRow?.activityRealId,
      activityPlanId: selectedRow?.activityPlanId,
      activityDiverseId: selectedRow?.activityDiverseId,
      realStart: selectedRow?.realStart,
      maintenanceType: selectedRow?.maintenanceType,
    },
    validationSchema,
    onSubmit: formValues => {
      // se for enviado com sucesso
      setLoadingSaveForm(true);
      postStartDailyBulletin(formValues)
        .then(() => {
          // solicita um reload de dados
          if (reloadData) reloadData();
          // fecha o formulário
          onClose();
        })
        .finally(() => setLoadingSaveForm(false));
    },
  });

  const handleDateChange = React.useCallback(
    (name, value) => {
      formik.setFieldTouched(name);
      formik.setFieldValue(name, value);
    },
    [formik],
  );

  const updateDate = React.useCallback(
    (fieldName, newDate, format) => {
      const date = dayjs(newDate, format, true);

      if (date.isValid()) {
        handleDateChange(fieldName, date.toISOString());
      } else {
        handleDateChange(fieldName, newDate);
      }

      if (!newDate) {
        handleDateChange(fieldName, null);
      }
    },
    [handleDateChange],
  );

  React.useEffect(() => {
    if (formik.dirty && !open) {
      formik.resetForm();
    }
  }, [formik, open]);

  const renderBody = React.useCallback(() => {
    return (
      <div className={classes.formScroll}>
        <form id={FORM_ID} onSubmit={formik.handleSubmit}>
          <Grid>
            <Typography className={classes.formMessage}>
              Preencha os dados obrigatórios para iniciar a(s) atividade(s):
            </Typography>
          </Grid>

          <Grid container className={classes.bodyContent}>
            <Input
              name="activityRealId"
              value={selectedRow?.activityRealId}
              inputProps={{ type: 'hidden' }}
              onChange={formik.handleChange}
            />

            <Input
              name="activityPlanId"
              value={selectedRow?.activityPlanId}
              inputProps={{ type: 'hidden' }}
              onChange={formik.handleChange}
            />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="activity"
                  label="Atividade"
                  variant="outlined"
                  value={selectedRow?.activity}
                  disabled
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <DatePicker
                  name="planStart"
                  label="Início previsto"
                  inputVariant="outlined"
                  value={selectedRow?.planStart}
                  disabled
                  onChange={formik.handleChange}
                  format="DD/MM"
                />
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  name="planCompletion"
                  label="Término previsto"
                  inputVariant="outlined"
                  value={selectedRow?.planCompletion}
                  disabled
                  onChange={formik.handleChange}
                  format="DD/MM"
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <DatePicker
                  name="replanStart"
                  label="Início replanejado"
                  inputVariant="outlined"
                  value={selectedRow?.replanStart}
                  disabled
                  onChange={formik.handleChange}
                  format="DD/MM"
                />
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  name="replanCompletion"
                  label="Término replanejado"
                  inputVariant="outlined"
                  value={selectedRow?.replanCompletion}
                  disabled
                  onChange={formik.handleChange}
                  format="DD/MM"
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="equipment"
                  label="Equipamento"
                  variant="outlined"
                  value={selectedRow?.equipment}
                  disabled
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <KeyboardDateTimePicker
                  name="realStart"
                  label="Início real"
                  inputVariant="outlined"
                  format="DD/MM/YY - HH:mm"
                  cancelLabel="Cancelar"
                  clearLabel="Limpar"
                  clearable
                  ampm={false}
                  maxDate={MAX_DATE}
                  onChange={(date, value) => {
                    updateDate(`realStart`, value, 'DD/MM/YY - HH:mm');
                  }}
                  value={
                    formik.values.realStart || selectedRow?.realStart || null
                  }
                  helperText={
                    (getIn(formik.touched, `realStart`) &&
                      getIn(formik.errors, `realStart`)) ||
                    DEFAULT_HELPER_TEXT
                  }
                  error={
                    getIn(formik.touched, `realStart`) &&
                    Boolean(getIn(formik.errors, `realStart`))
                  }
                />
              </Grid>
            </Grid>
          </Grid>
        </form>
      </div>
    );
  }, [
    classes.formScroll,
    classes.formMessage,
    classes.bodyContent,
    formik.handleSubmit,
    formik.handleChange,
    formik.values.realStart,
    formik.touched,
    formik.errors,
    selectedRow?.activityRealId,
    selectedRow?.activityPlanId,
    selectedRow?.activity,
    selectedRow?.planStart,
    selectedRow?.planCompletion,
    selectedRow?.replanStart,
    selectedRow?.replanCompletion,
    selectedRow?.equipment,
    selectedRow?.realStart,
    updateDate,
  ]);

  const renderFooter = React.useCallback(() => {
    return (
      <div className={classes.footer}>
        <Button color="primary" onClick={onClose}>
          Cancelar
        </Button>
        <ButtonWithLoading
          disabled={!formik.dirty || !formik.isValid}
          form={FORM_ID}
          type="submit"
          color="primary"
          isLoading={loadingSaveForm}
        >
          Salvar
        </ButtonWithLoading>
      </div>
    );
  }, [classes.footer, formik.dirty, formik.isValid, loadingSaveForm, onClose]);

  return (
    <>
      <MuiPickersUtilsProvider locale={ptBR} utils={DayjsUtils}>
        <ActionsButtonsBaseModal
          title="Iniciar"
          body={renderBody()}
          footer={renderFooter()}
          open={open}
          onClose={onClose}
          width="628px"
        >
          {children}
        </ActionsButtonsBaseModal>
      </MuiPickersUtilsProvider>
    </>
  );
};
export default DailyBulletinModalStart;
